import type { JSX } from 'react';

// Decorative jewelry motifs for the auth screen. Pure SVG so it scales, themes,
// and doesn't ship a binary asset. All decorative — aria-hidden + pointer-events-none.
export function JewelryBackdrop(): JSX.Element {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Tiled gold filigree pattern. Very low opacity so the form stays readable. */}
      <svg
        className="absolute inset-0 h-full w-full text-amber-500/10 dark:text-amber-400/[0.06]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="filigree"
            width="120"
            height="120"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(15)"
          >
            <path
              d="M60 20 C 75 35, 75 55, 60 70 C 45 55, 45 35, 60 20 Z M60 70 C 75 85, 75 105, 60 120 M60 70 C 45 85, 45 105, 60 120 M30 60 C 45 45, 65 45, 80 60 C 65 75, 45 75, 30 60 Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="0.8"
            />
            <circle cx="60" cy="60" r="2" fill="currentColor" />
            <circle cx="0" cy="60" r="1.5" fill="currentColor" />
            <circle cx="120" cy="60" r="1.5" fill="currentColor" />
          </pattern>
          <radialGradient id="vignette" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="100%" stopColor="white" stopOpacity="1" />
          </radialGradient>
          <mask id="vignetteMask">
            <rect width="100%" height="100%" fill="white" />
            <rect width="100%" height="100%" fill="url(#vignette)" />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="url(#filigree)" mask="url(#vignetteMask)" />
      </svg>

      {/* Top-left: diamond/gem cluster */}
      <svg
        className="absolute -left-10 -top-12 h-56 w-56 text-amber-500/25 dark:text-amber-400/15 sm:h-72 sm:w-72"
        viewBox="0 0 200 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round">
          <path d="M70 70 L100 40 L130 70 L100 130 Z" fill="currentColor" fillOpacity="0.18" />
          <path d="M70 70 L100 70 L100 40 Z M130 70 L100 70 L100 40 Z M100 70 L100 130 Z" />
          <path d="M40 90 L60 70 L75 90 L57 130 Z" fill="currentColor" fillOpacity="0.12" />
          <path d="M40 90 L57 90 L60 70 Z M75 90 L57 90 L60 70 Z M57 90 L57 130 Z" />
          <circle cx="155" cy="50" r="3" fill="currentColor" />
          <circle cx="170" cy="80" r="2" fill="currentColor" />
          <circle cx="40" cy="40" r="2" fill="currentColor" />
        </g>
      </svg>

      {/* Bottom-right: bangle/ring with gem */}
      <svg
        className="absolute -bottom-16 -right-12 h-72 w-72 text-amber-600/20 dark:text-amber-400/12 sm:h-96 sm:w-96"
        viewBox="0 0 240 240"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g stroke="currentColor" strokeWidth="2.5">
          <circle cx="120" cy="130" r="78" />
          <circle cx="120" cy="130" r="68" strokeWidth="1" strokeDasharray="2 4" />
          <path d="M105 50 L120 30 L135 50 L120 80 Z" fill="currentColor" fillOpacity="0.25" />
          <path d="M105 50 L135 50 M120 30 L120 80" strokeWidth="1.5" />
        </g>
      </svg>

      {/* Right edge: chain garland */}
      <svg
        className="absolute right-0 top-1/3 hidden h-72 w-24 text-amber-500/15 dark:text-amber-400/10 sm:block"
        viewBox="0 0 60 200"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g stroke="currentColor" strokeWidth="1.5">
          <ellipse cx="30" cy="20" rx="10" ry="6" />
          <ellipse cx="30" cy="32" rx="6" ry="10" />
          <ellipse cx="30" cy="50" rx="10" ry="6" />
          <ellipse cx="30" cy="62" rx="6" ry="10" />
          <ellipse cx="30" cy="80" rx="10" ry="6" />
          <ellipse cx="30" cy="92" rx="6" ry="10" />
          <ellipse cx="30" cy="110" rx="10" ry="6" />
          <ellipse cx="30" cy="122" rx="6" ry="10" />
          <ellipse cx="30" cy="140" rx="10" ry="6" />
          <ellipse cx="30" cy="152" rx="6" ry="10" />
          <ellipse cx="30" cy="170" rx="10" ry="6" />
        </g>
      </svg>

      {/* Sparkle stars scattered */}
      <svg
        className="absolute inset-0 h-full w-full text-amber-400/40 dark:text-amber-300/30"
        viewBox="0 0 800 600"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g fill="currentColor">
          <Sparkle cx={120} cy={120} size={6} />
          <Sparkle cx={680} cy={180} size={8} />
          <Sparkle cx={210} cy={460} size={5} />
          <Sparkle cx={620} cy={500} size={7} />
          <Sparkle cx={400} cy={70} size={4} />
          <Sparkle cx={750} cy={340} size={5} />
          <Sparkle cx={60} cy={320} size={6} />
        </g>
      </svg>
    </div>
  );
}

function Sparkle({ cx, cy, size }: { cx: number; cy: number; size: number }): JSX.Element {
  const s = size;
  return (
    <path
      d={`M${cx} ${cy - s} L${cx + s * 0.3} ${cy - s * 0.3} L${cx + s} ${cy} L${cx + s * 0.3} ${cy + s * 0.3} L${cx} ${cy + s} L${cx - s * 0.3} ${cy + s * 0.3} L${cx - s} ${cy} L${cx - s * 0.3} ${cy - s * 0.3} Z`}
    />
  );
}
